import {
    Banknote,
    BarChart2,
    Calculator,
    ChevronDown,
    ChevronUp,
    Info,
    Percent,
    PiggyBank,
    Settings,
    Timer
} from "lucide-react"
import {motion} from "framer-motion"
import {StableTooltip} from "@/components/ui/stable-tooltip"
import {IconTile} from "@/components/IconTile"
import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card"
import {Button} from "@/components/ui/button"
import {Slider} from "@/components/ui/slider"
import React, {useCallback, useEffect, useMemo, useState} from "react"
import {debounce} from "lodash"
import {CumulativeInvestmentChart} from "@/components/charts/CumulativeInvestmentChart";
import {calculateInvestmentBreakdown as calculatePricingBreakdown, usePricingData} from '@/hooks/usePricingData'

import {
    buildInvestmentBreakdown,
    formatCurrency,
    formatMonths,
    formatPercentage
} from '@/utils/financialSummaryHelpers.ts'
import {FinancialSummaryProps} from "@shared/interfaces/financialSummaryProps.interface.ts";
import {calculateFinancialProjection, getNPV, getROI, getPaybackMonths} from "@/utils/financialSummary/finance.ts"
import { normalizeYearlyCosts } from "@shared/constants/projectYearlyCosts.ts";

interface MetricCardProps {
  icon: React.ReactNode
  title: string
  value: string
  tooltip: string
}

/**
 * FinancialSummary - Displays financial metrics cards and cumulative investment vs. savings visualization when pricing is enabled
 *
 * This component renders ROI, NPV, and Payback Period metrics cards
 * plus the compelling cumulative investment vs. savings timeline chart and expandable projection table
 * using authentic data from the valueSummary blob. Only displays when pricingEnabled === true.
 */
export default function FinancialSummary({
  valueSummary,
  annualSavings,
  discountSoftwarePercent = 0,
  discountServicesPercent = 0,
  showDiscountSliders = false,
  onToggleDiscountSliders,
  onDiscountSoftwareChange,
  onDiscountServicesChange,
  currentRevenueBand,
  currentEntityCount,
  currentSelectedPillars,
  yearlyCosts,
}: FinancialSummaryProps) {
  // Create debounced handlers for slider changes to prevent API call flooding
  const debouncedSoftwareChange = useCallback(
    debounce((value: number) => {
      onDiscountSoftwareChange?.(value);
    }, 500),
    [onDiscountSoftwareChange]
  );

  const debouncedServicesChange = useCallback(
    debounce((value: number) => {
      onDiscountServicesChange?.(value);
    }, 500),
    [onDiscountServicesChange]
  );

  // Clean up debounced functions on unmounting
  useEffect(() => {
    return () => {
      debouncedSoftwareChange.cancel();
      debouncedServicesChange.cancel();
    };
  }, [debouncedSoftwareChange, debouncedServicesChange]);


  // ✨ Standardized MetricCard component with a consistent UI framework and pillar filtering indicator
  const MetricCard = ({ icon, title, value, tooltip, dataCy }: MetricCardProps & { dataCy?: string }) => (
    <StableTooltip
      content={`${tooltip} Always calculated using selected pillars only (${selectedPillars.join(', ')})`}
      side="top"
    >
      <div
        className="metric-card cursor-help group"
        style={{
          border: '2px solid rgba(20, 184, 166, 0.25)',
          boxShadow: '0 0 12px rgba(20, 184, 166, 0.2), 0 0 6px rgba(20, 184, 166, 0.1)',
          background: 'rgba(51, 65, 85, 0.05)'
        }}
      >
        <div className="metric-card-header">
          <div className="metric-card-icon">
            {icon}
          </div>
          <div className="metric-card-badge">
            <Info className="w-4 h-4 text-slate-500 group-hover:text-slate-400 lucide-icon-muted transition-colors duration-200" />
          </div>
        </div>
        <div className="metric-card-label">
          {title}
        </div>
        <div className="metric-card-value" data-cy={dataCy}>
          {value}
        </div>
      </div>
    </StableTooltip>
  )

  // State for expandable projection table
  const [showProjectionTable, setShowProjectionTable] = useState(false)

  // Get pricing data from API
  const { data: pricingData } = usePricingData()

  // Use current user selections (passed as props) OR fall back to saved data in valueSummary
  const revenueBand = currentRevenueBand || valueSummary?.kickoff?.revenueBand || 'Emerging'
  const entityCount = currentEntityCount || valueSummary?.kickoff?.legalEntities || 10
  
    // Prefer saved selections on the ValueSummary object; then nested pillarSelection; then currentSelectedPillars prop; finally a safe default
    const selectedPillars =
        (valueSummary?.selectedPillars?.length ? valueSummary.selectedPillars
            : valueSummary?.pillarSelection?.selectedPillars?.length ? valueSummary.pillarSelection.selectedPillars
                : currentSelectedPillars?.length ? currentSelectedPillars
                    : ['Journal Entry Automation', 'Account Reconciliation']);


  const investmentBreakdown = useMemo(
        () => buildInvestmentBreakdown({
            revenueBand, selectedPillars, entityCount,discountSoftwarePercent,
            discountServicesPercent, pricingData, calculatePricingBreakdown,
            valueSummary
        }),
        [revenueBand, selectedPillars, entityCount, discountSoftwarePercent, discountServicesPercent, pricingData, currentRevenueBand, currentEntityCount, currentSelectedPillars]
    );

  const normalizedYearlyCosts = useMemo(() => {
    if (!yearlyCosts) return undefined;
    return normalizeYearlyCosts(yearlyCosts);
  }, [yearlyCosts]);

  const filledYearlyCosts = useMemo(() => {
    if (!normalizedYearlyCosts) return undefined;
    const fillForward = (arr: number[]) => {
      const copy = Array.from({ length: 5 }, (_, idx) => Number(arr[idx] ?? 0) || 0);
      if (copy[4] === 0 && copy.slice(0, 4).some(v => v > 0)) {
        const lastNonZero = [...copy].reverse().find(v => v > 0) ?? 0;
        copy[4] = copy[3] || lastNonZero;
      }
      return copy;
    };
    return {
      software: fillForward(normalizedYearlyCosts.software || []),
      services: fillForward(normalizedYearlyCosts.services || []),
      total: fillForward(normalizedYearlyCosts.total || []),
    };
  }, [normalizedYearlyCosts]);

  const yearlyInvestmentOverrides = useMemo(() => {
    if (!filledYearlyCosts?.total || !Array.isArray(filledYearlyCosts.total)) return undefined;
    return Array.from({ length: 5 }, (_, idx) => Number(filledYearlyCosts.total?.[idx] ?? 0) || 0);
  }, [filledYearlyCosts]);

  const yearlyRampPercentages = useMemo(() => {
    const rampByPillar = (valueSummary as any)?.rampByPillarYear as Record<string, number[]> | undefined;
    if (!rampByPillar || selectedPillars.length === 0) {
      return [100, 100, 100, 100, 100];
    }

    return Array.from({ length: 5 }, (_, idx) => {
      let sum = 0;
      let count = 0;
      for (const pillar of selectedPillars) {
        const ramp = rampByPillar[pillar];
        const raw = Number(ramp?.[idx]);
        sum += Number.isFinite(raw) ? raw : 100;
        count += 1;
      }
      return count > 0 ? sum / count : 100;
    });
  }, [selectedPillars, valueSummary]);



  // Calculate payback period (Excel-style: first two periods, costs/savings * 12)
  const projectionData = calculateFinancialProjection(
    investmentBreakdown,
    annualSavings,
    yearlyInvestmentOverrides,
    yearlyRampPercentages
  )
  const paybackValue = getPaybackMonths(projectionData)

  const roiValue = getROI(projectionData)


  const breakEvenYear = paybackValue ? Math.ceil(paybackValue / 12) : null
  const cashFlows = projectionData.map(y => y.netBenefit);
  // Defensive check: If no cash flows (missing automation data), NPV = 0
  const npvValue = cashFlows.length > 0 ? getNPV(0.08, cashFlows) : 0;

  return (
    <div
      className="financial-summary-component space-y-6"
      data-testid="financial-summary"
    >
      {/* Financial Metrics Cards - Keep the existing layout */}
      <div id="export-section-financial-summary">
        <div className="flex items-center gap-3 mb-4">
          <IconTile icon={Banknote} color="teal-400" bg="slate-800" />
          <h2 className="text-lg font-semibold text-white">Financial Summary</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 element-spacing">
          <MetricCard
            icon={<Percent className="w-5 h-5" />}
            title="ROI"
            value={formatPercentage(roiValue)}
            tooltip="Return on Investment calculated as (Total Benefits - Total Investment) / Total Investment × 100"
            dataCy="roi-metric-value"
          />

          <MetricCard
            icon={<PiggyBank className="w-5 h-5" />}
            title="NET PRESENT VALUE"
            value={formatCurrency(npvValue)}
            tooltip="Present value of future cash flows ($918,500 per year for 5 years) minus initial investment, discounted at 8% annually"
          />

          <MetricCard
            icon={<Timer className="w-5 h-5" />}
            title="PAYBACK PERIOD"
            value={formatMonths(paybackValue)}
            tooltip="Time required to recover the initial investment through cumulative savings"
            dataCy="payback-metric-value"
          />
        </div>
      </div>

      {/* Investment vs. Savings Timeline - Export Section */}
      {projectionData.length > 0 && (
        <div id="export-section-investment" className="bg-slate-900/50 rounded-lg border border-slate-700/50 p-6">
          <motion.div
            data-chart-id="CumulativeInvestmentSavingsChart"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <Card className="border-2 border-teal-500/40 shadow-xl shadow-teal-900/40 bg-gradient-to-br from-slate-800/80 to-slate-900/90 backdrop-blur-sm">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <IconTile icon={BarChart2} color="teal-400" bg="slate-800" size={36} iconSize={36} />
                <div>
                  <CardTitle className="text-lg font-bold text-slate-200">Investment vs Savings Timeline</CardTitle>
                  <p className="text-xs uppercase tracking-wide text-slate-400">5-year financial projection with break-even analysis</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Animated Combo Chart */}
              <motion.div
                className="h-80"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <CumulativeInvestmentChart
                  data={projectionData}
                  breakEvenYear={breakEvenYear}
                  paybackMonths={paybackValue}
                />
              </motion.div>
            </CardContent>
          </Card>
          </motion.div>
        </div>
      )}

      {/* Five-Year Financial Projection - Export Section */}
      {projectionData.length > 0 && (
        <div id="export-section-5yr-table" className="bg-slate-900/50 rounded-lg border border-slate-700/50 p-6">
          <Card className="border-2 border-teal-500/30 shadow-xl shadow-teal-900/30 bg-gradient-to-br from-slate-800/60 to-slate-900/80">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-bold text-foreground">5-Year Financial Projection</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                data-cy="projection-toggle"
                onClick={() => setShowProjectionTable(!showProjectionTable)}
              >
                {showProjectionTable ? (
                  <>
                    <ChevronUp className="w-4 h-4 mr-2" />
                    Hide Details
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4 mr-2" />
                    Show Details
                  </>
                )}
              </Button>
            </div>
          </CardHeader>
          
          {showProjectionTable && (
            <CardContent className="space-y-6">
              {/* 5-Year Projection Table - Moved BEFORE Investment Breakdown */}
              <div className="overflow-x-auto">
                <table className="enhanced-table text-sm border-collapse" data-cy="projection-table">
                  <thead className="enhanced-table-header">
                    <tr>
                      <th className="text-left">Year</th>
                      <th className="text-right">Software</th>
                      <th className="text-right">Services</th>
                      <th className="text-right">Investment</th>
                      <th className="text-right">Annual Savings</th>
                      <th className="text-right">Net Benefit</th>
                      <th className="text-right">Cumulative Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projectionData.map((row, index) => {
                      const yearIdx = ((row as any)?.yearNumber ?? (index + 1)) - 1;
                      const softwareOverrideRaw = filledYearlyCosts?.software?.[yearIdx];
                      const servicesOverrideRaw = filledYearlyCosts?.services?.[yearIdx];
                      const hasSoftwareOverride = softwareOverrideRaw !== undefined && softwareOverrideRaw !== null;
                      const hasServicesOverride = servicesOverrideRaw !== undefined && servicesOverrideRaw !== null;
                      const softwareOverride = hasSoftwareOverride ? Number(softwareOverrideRaw) || 0 : undefined;
                      const servicesOverride = hasServicesOverride ? Number(servicesOverrideRaw) || 0 : undefined;

                      const softwareCost = hasSoftwareOverride
                        ? softwareOverride ?? 0 // allow 0 overrides to surface explicitly
                        : ((row as any)?.investmentDetails
                            ? Number((row as any).investmentDetails.platform || 0)
                              + Number((row as any).investmentDetails.pillars || 0)
                              + Number((row as any).investmentDetails.entities || 0)
                            : 0);

                      const servicesCost = hasServicesOverride
                        ? servicesOverride ?? 0 // allow 0 overrides to surface explicitly
                        : ((row as any)?.investmentDetails
                            ? Number((row as any).investmentDetails.setup || 0)
                            : 0);

                      return (
                        <tr key={index} className="enhanced-table-row expanded-table-row" data-cy={`projection-row-${index}`}>
                          <td className="enhanced-table-cell font-semibold text-slate-200 leading-5">{row.year}</td>
                          <td className="enhanced-table-cell-number text-slate-300">
                            {softwareCost > 0 ? formatCurrency(softwareCost) : '—'}
                          </td>
                          <td className="enhanced-table-cell-number text-slate-300">
                            {servicesCost > 0 ? formatCurrency(servicesCost) : '—'}
                          </td>
                          <td className="enhanced-table-cell-number text-slate-300">
                            {row.investment > 0 ? formatCurrency(row.investment) : '—'}
                          </td>
                          <td className="enhanced-table-cell-number enhanced-savings-value font-semibold">
                            {formatCurrency(row.savings)}
                          </td>
                          <td className="enhanced-table-cell-number">
                            <span className={row.netBenefit >= 0 ? 'enhanced-positive-value' : 'enhanced-negative-value'}>
                              {formatCurrency(row.netBenefit)}
                            </span>
                          </td>
                          <td className="enhanced-table-cell-number font-bold">
                            <span className={row.cumulativeValue >= 0 ? 'enhanced-cumulative-positive' : 'enhanced-negative-value'}>
                              {formatCurrency(row.cumulativeValue)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Investment Summary Breakdown Section (hidden) */}
              {false && (
              <div className="bg-slate-800/50 rounded-lg p-6 border border-slate-700/50">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-slate-200 flex items-center gap-2">
                    <Calculator className="w-5 h-5 text-teal-400" />
                    Investment Summary Breakdown
                  </h3>
                  {/* Add the Show Discounts button here for user */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onToggleDiscountSliders}
                    className="w-8 h-8 p-0 text-gray-700 hover:text-gray-600 hover:bg-gray-800/5 transition-all duration-200 opacity-40 hover:opacity-60"
                    title="Apply Discounts"
                  >
                    <Settings className="w-3 h-3" />
                  </Button>
                </div>
                
                {/* Discount Sliders Section - Positioned right below the Apply Discounts button */}
                {showDiscountSliders && (
                  <div className="mt-4 mb-6 bg-slate-900 dark:bg-slate-900 border-2 border-teal-500 rounded-lg p-6 space-y-6 shadow-lg">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-medium text-teal-400">
                        Internal Discount Adjustments
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={onToggleDiscountSliders}
                        className="text-xs text-slate-400 hover:text-slate-200"
                      >
                        ✕ Close
                      </Button>
                    </div>
                    
                    {/* Software Discount Slider */}
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <label className="text-sm font-medium text-slate-300">
                          Software Discount
                        </label>
                        <span className="text-sm text-slate-400">
                          {(discountSoftwarePercent * 100).toFixed(0)}%
                        </span>
                      </div>
                      <Slider
                        value={[discountSoftwarePercent * 100]}
                        onValueChange={(value) => debouncedSoftwareChange(value[0] / 100)}
                        max={40}
                        step={1}
                        className="w-full"
                      />
                    </div>
                    
                    {/* Services Discount Slider */}
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <label className="text-sm font-medium text-slate-300">
                          Services Discount
                        </label>
                        <span className="text-sm text-slate-400">
                          {(discountServicesPercent * 100).toFixed(0)}%
                        </span>
                      </div>
                      <Slider
                        value={[discountServicesPercent * 100]}
                        onValueChange={(value) => debouncedServicesChange(value[0] / 100)}
                        max={40}
                        step={1}
                        className="w-full"
                      />
                    </div>
                    
                    {/* Notice about real-time updates */}
                    <div className="mt-4 p-3 bg-slate-800/50 rounded-lg">
                      <p className="text-xs text-slate-400 italic">
                        Discounts dynamically adjust ROI, NPV, and Payback calculations in real-time
                      </p>
                    </div>
                  </div>
                )}
                
                <div className="space-y-6">
                  {/* Year 1 Costs */}
                  <div className="border-t border-slate-700/30 pt-4">
                    <h4 className="text-sm font-semibold text-slate-300 mb-3">Year 1 Costs</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-400">Software (5x annual)</span>
                        <span className="text-base font-mono text-right">{formatCurrency(investmentBreakdown.annualRecurringCosts * 5)}</span>
                      </div>
                      
                      {/* Software Discount Row - Show only if discount > 0% */}
                      {discountSoftwarePercent > 0 && typeof investmentBreakdown.originalAnnualSubscription === 'number' && (
                        <div className="flex justify-between items-center pl-4">
                          <span className="text-sm text-red-400">
                            Software Discount ({(discountSoftwarePercent * 100).toFixed(0)}%)
                          </span>
                          <span className="text-base font-mono text-red-400">–{formatCurrency(investmentBreakdown.originalAnnualSubscription! - investmentBreakdown.annualRecurringCosts)}</span>
                        </div>
                      )}
                      
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-400">Services (2x annual)</span>
                        <span className="text-base font-mono text-right">{formatCurrency(investmentBreakdown.year1SetupCosts)}</span>
                      </div>
                      
                      {/* Services Discount Row - Show only if discount > 0% */}
                      {discountServicesPercent > 0 && typeof investmentBreakdown.originalServicesCost === 'number' && (
                        <div className="flex justify-between items-center pl-4">
                          <span className="text-sm text-red-400">
                            Services Discount ({(discountServicesPercent * 100).toFixed(0)}%)
                          </span>
                          <span className="text-base font-mono text-red-400">–{formatCurrency(investmentBreakdown.originalServicesCost! - investmentBreakdown.year1SetupCosts)}</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center pt-2 border-t border-slate-700/30">
                        <span className="text-sm font-semibold text-slate-300">Total Year 1 Investment</span>
                        <span className="text-base font-bold font-mono text-right investment-total-value">{formatCurrency(investmentBreakdown.year1Investment)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Annual Software Costs */}
                  <div className="border-t border-slate-700/30 pt-4">
                    <h4 className="text-sm font-semibold text-slate-300 mb-3">Annual Software Costs </h4>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-400">Platform License ({investmentBreakdown.revenueBand})</span>
                        <span className="text-base font-mono text-right">{formatCurrency(investmentBreakdown.platform)}/year</span>
                      </div>
                      {investmentBreakdown.pillars.map((pillar: any, index: number) => (
                        <div key={index} className="flex justify-between items-center">
                          <span className="text-sm text-slate-400">{pillar.name}</span>
                          <span className="text-base font-mono text-right">{formatCurrency(pillar.cost)}/year</span>
                        </div>
                      ))}
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-400">Entity Subscription ({investmentBreakdown.entityCount} ent.)</span>
                        <span className="text-base font-mono text-right">{formatCurrency(investmentBreakdown.discountedEntityCosts)}/year</span>
                      </div>
                      <div className="flex justify-between items-center pt-2 border-t border-slate-700/30">
                        <span className="text-sm font-semibold text-slate-300">Annual Software Costs</span>
                        <span className="text-base font-bold font-mono text-right investment-total-value">{formatCurrency(investmentBreakdown.annualRecurringCosts)}/year</span>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
              )}


            </CardContent>
          )}
          </Card>
        </div>
      )}
    </div>
  )
}
